<template>
	<div class="video-tile" :class="{ mirrored, 'fit-cover': fit === 'cover' }">
		<template v-if="stream">
			<!-- Shown behind a portrait picture (the parent sets .portrait): a blurred, scaled copy of the same video fills the tile -->
			<video v-if="fit === 'contain'" ref="backdropEl" class="backdrop" autoplay playsinline muted disablepictureinpicture aria-hidden="true"></video>
			<video ref="pictureEl" class="picture" autoplay playsinline muted disablepictureinpicture @resize="reportAspect" @loadedmetadata="reportAspect"></video>
		</template>
		<div v-else class="placeholder">
			<slot name="placeholder">
				<div class="initials">{{ placeholder }}</div>
			</slot>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

interface Props {
	/** The video to show - without one the tile shows its placeholder */
	stream?: MediaStream
	/** Mirror the picture, as for my own camera */
	mirrored?: boolean
	/** 'contain' shows the whole picture (a portrait one over a blurred copy of itself), 'cover' fills the tile */
	fit: 'contain' | 'cover'
	/** Shown in a circle while there is no video, e.g. the person's initials - the placeholder slot replaces it */
	placeholder: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
	/** The video's shape, videoWidth / videoHeight, whenever it changes - a phone turned upright reports below 1 */
	aspect: [value: number]
}>()

const pictureEl = ref<HTMLVideoElement>()
const backdropEl = ref<HTMLVideoElement>()

/**
 * Show the stream in a video element
 * @param video The element, undefined while it isn't rendered
 * @param stream The stream to show
 */
function show(video: HTMLVideoElement | undefined, stream: MediaStream | undefined) {
	if (!video) { return }

	// The call's audio has its own player: a video element must never play it a second time
	video.muted = true
	if (video.srcObject !== (stream ?? null)) { video.srcObject = stream ?? null }
	if (stream) {
		video.play().catch(() => { /* a muted video may always play - a refusal just leaves the last frame */ })
	}
}

/**
 * Keep a video element on the current stream, and let go of the stream in an element that is gone
 * @param element The element's template ref
 */
function follow(element: typeof pictureEl) {
	watch([element, () => props.stream], ([video, stream], [previous]) => {
		if (previous && previous !== video) { previous.srcObject = null }
		show(video, stream)
	})
}

follow(pictureEl)
follow(backdropEl)

/**
 * Report the video's shape: the other party turning their phone changes it mid-call
 * @param event The video's resize or loadedmetadata event
 */
function reportAspect(event: Event) {
	const video = event.target as HTMLVideoElement

	if (video.videoWidth > 0 && video.videoHeight > 0) {
		emit('aspect', video.videoWidth / video.videoHeight)
	}
}
</script>

<style scoped>
	.video-tile {
		position: relative;
		width: 100%;
		height: 100%;
		overflow: hidden;
		background: #161616;
	}

	.video-tile video {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
	}

	.picture {
		object-fit: contain;
	}

	.fit-cover .picture {
		object-fit: cover;
	}

	.mirrored .picture {
		transform: scaleX(-1);
	}

	/* Hidden until the picture turns portrait, then faded in - so a phone turning over doesn't jump */
	.backdrop {
		object-fit: cover;
		filter: blur(24px);
		transform: scale(1.15);
		opacity: 0;
		visibility: hidden;
		transition: opacity 400ms ease, visibility 0s linear 400ms;
	}

	.video-tile.portrait .backdrop {
		opacity: 0.7;
		visibility: visible;
		transition: opacity 400ms ease, visibility 0s;
	}

	.placeholder {
		position: absolute;
		inset: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		color: #ddd;
	}

	.initials {
		width: 4.5rem;
		height: 4.5rem;
		border-radius: 50%;
		background: #333;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.6rem;
		font-weight: 200;
	}
</style>
